import React, { FC, useCallback, useMemo, useState } from 'react';
import {
  ITrajectoryDetailProgress,
  TrajectorySectionCode,
  TrajectorySectionOrWorksheetType,
  TrajectoryViewMode,
} from '../../../../config/api/models/Trajectory';
import {
  TrajectoryExercisesEvaluationBuilder,
  TrajectoryOpbouwTheoryBuilder,
} from '../TrajectoryDetail';
import TrajectoryTheoryOpbouwPageView from './TrajectoryTheoryOpbouwPageView';
import TrajectoryExercisesEvaluationPageView from './TrajectoryExercisesEvaluationPageView';
import { useParams } from 'react-router';
import { useUser } from '../../../shared';
import { GroupStudentSeriesScores, SeriesScores } from '../../../../config/api/models/Score';
import UserTrajectoryScoresHandler from './UserTrajectoryScoresHandler';
import StudentTrajectoryScoresHandler from './StudentTrajectoryScoresHandler';
import GroupTrajectoryScoresHandler from './GroupTrajectoryScoresHandler';
import { TrajectoryStudyGuidePageView } from '../study-guide/TrajectoryStudyGuidePageView';
import { TrajectoryStudyGuideBuilder } from '../study-guide/TrajectoryStudyGuideBuilder';
import { UrlParams } from '../../../shared/types';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { usePagesByType } from '../../hooks';
import { StudentFiltersEnum } from 'src/modules/shared/constants';

type Props = {
  sectionCode: TrajectorySectionCode;
  onSelectedSectionChange: (newSectionCode: TrajectorySectionCode) => void;
  hasTheory: boolean;
  hasExercises: boolean;
  progress?: ITrajectoryDetailProgress[];
  viewMode: TrajectoryViewMode;
  onPatchProgress: (nextSectionCode: TrajectorySectionCode) => void;
};

const TrajectoryViewSelectedSection: FC<Props> = ({
  sectionCode,
  onSelectedSectionChange,
  hasTheory,
  hasExercises,
  progress,
  viewMode,
  onPatchProgress,
}) => {
  const { isStudent, isTeacher } = useUser();
  const { seriesId } = useParams<UrlParams>();
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const selectedStudent = useAppSelector((state) => state.filters.selectedStudent);
  const activeFilter = useAppSelector((state) => state.filters.activeFilter);
  const [userScores, setUserScores] = useState<Record<string, SeriesScores> | undefined>(undefined);
  const [studentScores, setStudentScores] = useState<Record<string, SeriesScores> | undefined>(
    undefined
  );
  const [groupScores, setGroupScores] = useState<GroupStudentSeriesScores | undefined>(undefined);
  const [shouldReFetch, setShouldReFetch] = useState(false);
  const pageType: TrajectorySectionOrWorksheetType | null = useMemo(() => {
    switch (sectionCode) {
      case '0': return 'opbouw' ;
      case '1': return 'theory' ;
      case '2': return 'worksheet_exercises' ;
      default: return null;
    }
  }, [sectionCode]);
  const pages = usePagesByType(pageType);
  const pageId = useMemo(() => {
    return pages?.data && pages.data.length > 0 ? pages.data[0].id : undefined;
  }, [pages?.data]);

  const handleExpandedChanged = useCallback(() => {
    if (isStudent) setShouldReFetch(true);
  }, [isStudent]);

  return (
    <>
      {isStudent && activeFilter === StudentFiltersEnum.PROGRESS_AND_SCORES && (
        <UserTrajectoryScoresHandler
          pageId={pageId as string}
          onChange={(value) => {
            setShouldReFetch(false);
            setUserScores(value);
          }}
          shouldReFetch={shouldReFetch}
        />
      )}
      {isTeacher && (
        <div>
          {pageId && selectedGroup !== '' && selectedStudent !== '' && (
            <StudentTrajectoryScoresHandler
              pageId={pageId as string}
              onChange={(value) => {
                setShouldReFetch(false);
                setGroupScores(undefined);
                setStudentScores(value);
              }}
              shouldReFetch={shouldReFetch}
            />
          )}
          {pageId && selectedGroup !== '' && selectedStudent === '' && (
            <GroupTrajectoryScoresHandler
              pageId={pageId as string}
              onChange={(value) => {
                setShouldReFetch(false);
                setStudentScores(undefined);
                setGroupScores(value);
              }}
              shouldReFetch={shouldReFetch}
            />
          )}
        </div>
      )}
      {
        {
          0: {
            read: (
              <TrajectoryTheoryOpbouwPageView
                type="opbouw"
                onSelectedSectionChange={onSelectedSectionChange}
                hasTheory={hasTheory}
                hasExercises={hasExercises}
                progress={progress}
                onPatchProgress={onPatchProgress}
                trajectoryScoresByGroup={groupScores}
              />
            ),
            edit: <TrajectoryOpbouwTheoryBuilder type="opbouw" />,
          }[viewMode],
          1: {
            read: (
              <TrajectoryTheoryOpbouwPageView
                type="theory"
                onSelectedSectionChange={onSelectedSectionChange}
                hasTheory={hasTheory}
                hasExercises={hasExercises}
                progress={progress}
                onPatchProgress={onPatchProgress}
                trajectoryScoresByGroup={groupScores}
              />
            ),
            edit: <TrajectoryOpbouwTheoryBuilder type="theory" />,
          }[viewMode],
          2: {
            read: (
              <TrajectoryExercisesEvaluationPageView
                type="exercises"
                seriesId={seriesId as string}
                trajectoryScoresByUser={userScores}
                trajectoryScoresByStudent={studentScores}
                trajectoryScoresByGroup={groupScores}
                onExpandedChanged={handleExpandedChanged}
              />
            ),
            edit: (
              <TrajectoryExercisesEvaluationBuilder type="exercises" isTrajectoryViewEditMode />
            ),
          }[viewMode],
          3: null,
          4: {
            read: <TrajectoryStudyGuidePageView />,
            edit: <TrajectoryStudyGuideBuilder />,
          }[viewMode],
        }[sectionCode]
      }
    </>
  );
};

export default TrajectoryViewSelectedSection;
